'use client';

import { useRef } from 'react';
import { Link } from '@inertiajs/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { register } from '@/routes';
import { T } from './V2Tokens';
import { STATS } from './V1Data';
import { useIsMobile } from './useIsMobile';

gsap.registerPlugin(ScrollTrigger, useGSAP);

// ─── Campaign Mosaic ──────────────────────────────────────────────────────────
// Replaces the boring dark app window. Shows real AI marketing output:
// colorful content pieces the system produces for clients.

function CampaignMosaic() {
    return (
        <div style={{ position: 'relative', height: 480, userSelect: 'none' }}>

            {/* ── Card A: Instagram post (indigo, square) ── top-right */}
            <div className="mc-card-a" style={{
                position: 'absolute', top: 0, right: 30,
                width: 196, height: 196,
                borderRadius: 20,
                background: 'linear-gradient(145deg, #5B4FE5 0%, #7E67D6 60%, #A291E3 100%)',
                padding: '18px 16px',
                boxShadow: '0 28px 56px rgba(91,79,229,0.38), 0 0 0 1px rgba(91,79,229,0.2)',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
                {/* Profile row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
                    </div>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>@brand_sa</span>
                    <span style={{ marginRight: 'auto', fontSize: 9, color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.12)', padding: '2px 7px', borderRadius: 99, fontWeight: 600 }}>IG</span>
                </div>

                {/* Headline */}
                <div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', lineHeight: 1.2, letterSpacing: '-0.03em', marginBottom: 8 }}>
                        صيف 2025
                        <br />بدأ الآن
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {['#صيف_2025', '#عروض'].map(tag => (
                            <span key={tag} style={{ fontSize: 9, color: 'rgba(255,255,255,0.75)', background: 'rgba(255,255,255,0.14)', padding: '3px 8px', borderRadius: 99, fontWeight: 600 }}>
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Engagement strip */}
                <div style={{ display: 'flex', gap: 10, fontSize: 10, color: 'rgba(255,255,255,0.65)' }}>
                    <span>♥ 4.2K</span>
                    <span>↗ 840</span>
                </div>
            </div>

            {/* ── Card B: Growth metric badge (coral) ── top-left */}
            <div className="mc-card-b" style={{
                position: 'absolute', top: 24, left: 10,
                width: 152, height: 168,
                borderRadius: 18,
                background: T.coral,
                padding: '16px',
                boxShadow: '0 22px 48px rgba(255,138,101,0.38), 0 0 0 1px rgba(255,138,101,0.15)',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    متوسط الوصول
                </div>
                <div>
                    <div style={{ fontSize: 48, fontWeight: 900, color: '#fff', lineHeight: 1, letterSpacing: '-0.05em' }}>48K</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 4, fontWeight: 600 }}>
                        ↑ 340% نمو شهري
                    </div>
                </div>
                <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.2)' }}>
                    <div style={{ width: '72%', height: '100%', borderRadius: 2, background: 'rgba(255,255,255,0.7)' }} />
                </div>
            </div>

            {/* ── Card C: Email campaign (white) ── middle */}
            <div className="mc-card-c" style={{
                position: 'absolute', top: 218, left: 0,
                width: 278,
                borderRadius: 14,
                background: '#FAF8FF',
                boxShadow: '0 16px 36px rgba(14,11,46,0.12), 0 0 0 1px rgba(14,11,46,0.07)',
                overflow: 'hidden',
            }}>
                {/* Brand stripe */}
                <div style={{ height: 4, background: `linear-gradient(to left, ${T.brand}, ${T.brand300})` }} />
                <div style={{ padding: '14px 16px 16px' }}>
                    <div style={{ fontSize: 10, color: T.ink50, marginBottom: 5, fontWeight: 500 }}>حملة بريد إلكتروني</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 8, lineHeight: 1.35 }}>
                        مجرّد استديو — نيوزليتر الأسبوع
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 11, color: T.ink50 }}>
                        <span>4.8K مشترك</span>
                        <span style={{ color: T.mint, fontWeight: 700 }}>42% معدل فتح</span>
                    </div>
                </div>
            </div>

            {/* ── Card D: Ad banner (amber) ── bottom-right */}
            <div className="mc-card-d" style={{
                position: 'absolute', bottom: 10, right: 10,
                width: 218, height: 98,
                borderRadius: 14,
                background: T.amber,
                padding: '14px 18px',
                boxShadow: '0 18px 36px rgba(242,201,76,0.38), 0 0 0 1px rgba(242,201,76,0.2)',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
            }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(14,11,46,0.55)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>
                    إعلان ممول
                </div>
                <div style={{ fontSize: 28, fontWeight: 900, color: T.ink, letterSpacing: '-0.04em', lineHeight: 1 }}>
                    وفّر 40%
                </div>
                <div style={{ fontSize: 11, color: 'rgba(14,11,46,0.6)', marginTop: 4 }}>على خطة النمو هذا الشهر</div>
            </div>

            {/* ── Floating chip: published status ── */}
            <div className="mc-chip" style={{
                position: 'absolute', bottom: 30, left: 180,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 13px', borderRadius: 99,
                background: T.mint,
                boxShadow: '0 8px 20px rgba(111,227,193,0.35)',
                fontSize: 11, fontWeight: 700, color: '#0E0B2E',
                transform: 'rotate(-1.5deg)',
            }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#0E0B2E', opacity: 0.6 }} />
                نُشر تلقائياً
            </div>

            {/* ── AI generation badge ── */}
            <div className="mc-badge" style={{
                position: 'absolute', top: 204, right: 14,
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 99,
                background: 'rgba(14,11,46,0.88)',
                backdropFilter: 'blur(8px)',
                fontSize: 10, fontWeight: 700, color: '#fff',
                letterSpacing: '0.04em',
                boxShadow: '0 4px 12px rgba(14,11,46,0.3)',
            }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: T.brand300, animation: 'v2-pulse 2s ease-in-out infinite' }} />
                AI يعمل الآن
            </div>
        </div>
    );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

export function V2Hero() {
    const isMobile  = useIsMobile();
    const outerRef  = useRef<HTMLDivElement>(null);
    const innerRef  = useRef<HTMLElement>(null);
    const textRef   = useRef<HTMLDivElement>(null);
    const mosaicRef = useRef<HTMLDivElement>(null);
    const trustRef  = useRef<HTMLDivElement>(null);
    const glowRef   = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        if (isMobile) return;

        // ── Place all cards at mosaic center (computed from DOM measurements) ──
        // Each card's x/y offset = distance from its natural position to the
        // container's geometric center (297, 240). Cards start here, then scroll
        // "deals" them to their corners. GSAP owns all transforms; no CSS rotation.
        gsap.set('.mc-card-a', { x: -170, y: 142,  rotation: 0, opacity: 0 });
        gsap.set('.mc-card-b', { x:  212, y: 132,  rotation: 0, opacity: 0 });
        gsap.set('.mc-card-c', { x:  159, y:  -50, rotation: 0, opacity: 0 });
        gsap.set('.mc-card-d', { x: -179, y: -205, rotation: 0, opacity: 0 });
        gsap.set('.mc-chip',   { opacity: 0, scale: 0.85 });
        gsap.set('.mc-badge',  { opacity: 0, scale: 0.8  });

        // ── Entrance: text column staggers in ────────────────────────────
        const textEls = textRef.current ? Array.from(textRef.current.children) : [];
        gsap.fromTo(textEls,
            { y: 32, opacity: 0 },
            { y: 0, opacity: 1, stagger: 0.11, duration: 0.8, ease: 'power3.out', delay: 0.15 }
        );

        // ── Entrance: Card A glows in at center, trust bar and logos follow ─
        gsap.to('.mc-card-a', { opacity: 1, duration: 0.6, ease: 'power2.out', delay: 0.5 });
        gsap.fromTo(trustRef.current, { opacity: 0 }, { opacity: 1, duration: 0.55, ease: 'power2.out', delay: 1.0 });
        if (trustRef.current) {
            // Brand name spans live in the second child div's children
            const brandContainer = trustRef.current.children[1] as HTMLElement | undefined;
            if (brandContainer) {
                const brandSpans = Array.from(brandContainer.children) as HTMLElement[];
                gsap.fromTo(brandSpans,
                    { opacity: 0, y: 8 },
                    { opacity: 0.25, y: 0, stagger: 0.065, duration: 0.42, ease: 'power2.out', delay: 1.35 }
                );
            }
        }

        // ── Scroll story: each card "deals" from center to its corner ─────
        // scrub:1.8 — slight lag makes it feel physical.
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: outerRef.current,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 1.8,
            },
        });

        // Hold: Card A floats at center
        tl.to({}, { duration: 0.5 });

        // Card A deals to top-right corner (rotate into place)
        tl.to('.mc-card-a', { x: 0, y: 0, rotation: -2.5, duration: 1.4, ease: 'power3.out' });

        // Card B materialises at center while A is still moving, then deals left
        tl.to('.mc-card-b', { opacity: 1, duration: 0.35, ease: 'power2.out' }, '<0.35');
        tl.to('.mc-card-b', { x: 0, y: 0, rotation: 3.5, duration: 1.4, ease: 'power3.out' }, '<0.1');
        tl.to({}, { duration: 0.5 });

        // Card C materialises and deals to middle-left
        tl.to('.mc-card-c', { opacity: 1, duration: 0.35, ease: 'power2.out' });
        tl.to('.mc-card-c', { x: 0, y: 0, rotation: -1, duration: 1.2, ease: 'power3.out' }, '<0.1');
        tl.to({}, { duration: 0.5 });

        // Card D deals to bottom-right, chip + badge materialise
        tl.to('.mc-card-d', { opacity: 1, duration: 0.35, ease: 'power2.out' });
        tl.to('.mc-card-d', { x: 0, y: 0, rotation: 2, duration: 1.2, ease: 'power3.out' }, '<0.1');
        tl.to('.mc-chip',   { opacity: 1, scale: 1, duration: 0.5, ease: 'power2.out' }, '<0.4');
        tl.to('.mc-badge',  { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' }, '<0.15');
        tl.to({}, { duration: 0.7 });

        // ── Ambient glow: drifts with scroll + breathes in a loop ────────
        gsap.to(glowRef.current, {
            y: -60, x: 18, ease: 'none',
            scrollTrigger: {
                trigger: outerRef.current,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 2.5,
            },
        });
        gsap.to(glowRef.current, {
            scale: 1.18, x: 22, ease: 'sine.inOut',
            repeat: -1, yoyo: true, duration: 5.5,
        });

        // ── Text column drifts up slower than mosaic (parallax depth) ────
        if (!isMobile && textRef.current) {
            gsap.to(textRef.current, {
                y: -55, ease: 'none',
                scrollTrigger: {
                    trigger: outerRef.current,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 2.2,
                },
            });
        }

        // ── Mosaic block drifts at a different rate for layered parallax ──
        gsap.to(mosaicRef.current, {
            y: -40, ease: 'none',
            scrollTrigger: {
                trigger: outerRef.current,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 1.5,
            },
        });

    }, { scope: outerRef, dependencies: [isMobile] });

    if (isMobile) {
        return (
            <>
                <style>{`
                    @keyframes v2-pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.5); opacity: 0.5; }
                    }
                `}</style>
                <section
                    id="hero"
                    style={{
                        paddingTop: 64 + 32,
                        paddingBottom: 56,
                        paddingLeft: 20,
                        paddingRight: 20,
                        background: T.bgWhite,
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                    dir="rtl"
                >
                    <div aria-hidden="true" style={{
                        position: 'absolute', top: -80, right: '-10%',
                        width: 380, height: 380, borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(138,124,255,0.14), transparent 62%)',
                        filter: 'blur(60px)', pointerEvents: 'none',
                    }} />
                    <div style={{ position: 'relative' }}>
                        {/* Eyebrow */}
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            padding: '6px 14px', borderRadius: T.rPill,
                            background: T.bgCard, border: `1px solid ${T.line}`,
                            fontSize: 12, fontWeight: 600, color: T.ink70,
                            marginBottom: 20,
                        }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0, animation: 'v2-pulse 2.8s ease-in-out infinite' }} />
                            أول وكالة تسويق تعمل بالكامل بالذكاء الاصطناعي في السعودية
                        </span>

                        {/* Headline */}
                        <h1 style={{
                            fontSize: 'clamp(36px, 10vw, 56px)',
                            fontWeight: 900,
                            lineHeight: 1.08,
                            letterSpacing: '-0.035em',
                            margin: '0 0 16px',
                            color: T.ink,
                        }}>
                            وكالتك الكاملة.<br />
                            <span style={{ color: T.brand }}>بالذكاء الاصطناعي.</span><br />
                            <span style={{ fontSize: '0.5em', fontWeight: 600, color: T.ink70, letterSpacing: '-0.018em' }}>
                                من التحليل إلى النشر، Growth Engine يعمل وحده.
                            </span>
                        </h1>

                        {/* Sub */}
                        <p style={{
                            fontSize: 16, lineHeight: 1.72, color: T.ink70,
                            margin: '0 0 28px',
                        }}>
                            خمسة أقسام متكاملة — تحليل السوق، الاستراتيجية، الإنتاج الإبداعي، إدارة الحملات، وCRM —
                            تعمل كمنظومة واحدة لتحقيق نمو{' '}
                            <strong style={{ color: T.ink, fontWeight: 700 }}>حقيقي ومقيس</strong>.
                        </p>

                        {/* CTAs */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 36 }}>
                            <Link
                                href={register.url()}
                                style={{
                                    padding: '14px 24px', borderRadius: T.rPill,
                                    background: T.ink, color: '#FAF8FF',
                                    fontSize: 15, fontWeight: 700, textDecoration: 'none',
                                    textAlign: 'center',
                                }}
                            >
                                ابدأ Growth Engine مجاناً
                            </Link>
                            <a
                                href="#process"
                                style={{
                                    padding: '14px 24px', borderRadius: T.rPill,
                                    background: 'transparent', color: T.ink,
                                    border: `1px solid ${T.ink20}`,
                                    fontSize: 15, fontWeight: 600, textDecoration: 'none',
                                    textAlign: 'center',
                                }}
                            >
                                شاهد آلية العمل ↓
                            </a>
                        </div>

                        {/* Stats */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 8,
                            marginBottom: 32,
                        }}>
                            {STATS.map((s, i) => (
                                <div key={i} style={{ padding: '10px 0' }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: T.ink, letterSpacing: '-0.03em', lineHeight: 1 }}>{s.n}</div>
                                    <div style={{ fontSize: 10, color: T.ink50, marginTop: 4, lineHeight: 1.35 }}>{s.l}</div>
                                </div>
                            ))}
                        </div>

                        {/* Trust bar */}
                        <div style={{ paddingTop: 22, borderTop: `1px solid ${T.line}` }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: T.ink50, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>
                                يثق بنا أكثر من 80 عميل في المنطقة
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
                                {['STC', 'NEOM', 'noon', 'Tamara', 'Jahez', 'Mrsool'].map(b => (
                                    <span key={b} style={{ fontSize: 15, fontWeight: 800, color: T.ink, opacity: 0.25, letterSpacing: '-0.01em' }}>{b}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    return (
        <>
            <style>{`
                @keyframes v2-pulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.5); opacity: 0.5; }
                }
                @keyframes v2-dot-bounce {
                    0%, 80%, 100% { transform: translateY(0); opacity: 0.45; }
                    40% { transform: translateY(-5px); opacity: 1; }
                }
            `}</style>

            {/* Outer: 300vh tall — provides the scroll story window */}
            <div ref={outerRef} style={{ height: '300vh', position: 'relative' }}>

                {/* Inner: CSS sticky — pins at top for 200vh of scroll */}
                <section
                    ref={innerRef}
                    style={{
                        position: 'sticky',
                        top: 0,
                        height: '100vh',
                        overflow: 'hidden',
                        paddingTop: '64px',
                        paddingLeft: isMobile ? '20px' : '56px',
                        paddingRight: isMobile ? '20px' : '56px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    {/* Ambient glow blob */}
                    <div ref={glowRef} aria-hidden="true" style={{
                        position: 'absolute', top: -80, right: '8%',
                        width: 640, height: 640, borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(138,124,255,0.18), transparent 62%)',
                        filter: 'blur(72px)', pointerEvents: 'none', zIndex: 0,
                    }} />
                    {/* Secondary glow — bottom left */}
                    <div aria-hidden="true" style={{
                        position: 'absolute', bottom: -100, left: '10%',
                        width: 400, height: 400, borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,138,101,0.10), transparent 65%)',
                        filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0,
                    }} />

                    <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', position: 'relative', zIndex: 1 }}>

                        {/* Two-column grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr',
                            gap: isMobile ? 32 : 64,
                            alignItems: 'center',
                        }}>
                            {/* ── Text column (RIGHT in RTL) ── */}
                            <div ref={textRef}>
                                {/* Eyebrow */}
                                <div>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 8,
                                        padding: '6px 14px', borderRadius: T.rPill,
                                        background: T.bgCard, border: `1px solid ${T.line}`,
                                        fontSize: 12, fontWeight: 600, color: T.ink70,
                                    }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0, animation: 'v2-pulse 2.8s ease-in-out infinite' }} />
                                        أول وكالة تسويق تعمل بالكامل بالذكاء الاصطناعي في السعودية
                                    </span>
                                </div>

                                {/* Headline */}
                                <div>
                                    <h1 style={{
                                        fontSize: 'clamp(36px, 4.4vw, 68px)',
                                        fontWeight: 900,
                                        lineHeight: 1.08,
                                        letterSpacing: '-0.035em',
                                        margin: '22px 0 16px',
                                        color: T.ink,
                                    }}>
                                        وكالتك الكاملة.<br />
                                        <span style={{ color: T.brand }}>بالذكاء الاصطناعي.</span><br />
                                        <span style={{ fontSize: '0.5em', fontWeight: 600, color: T.ink70, letterSpacing: '-0.018em' }}>
                                            من التحليل إلى النشر، Growth Engine يعمل وحده.
                                        </span>
                                    </h1>
                                </div>

                                {/* Sub */}
                                <div>
                                    <p style={{
                                        fontSize: 16, lineHeight: 1.72, color: T.ink70,
                                        margin: '0 0 26px', maxWidth: 480,
                                    }}>
                                        خمسة أقسام متكاملة — تحليل السوق، الاستراتيجية، الإنتاج الإبداعي، إدارة الحملات، وCRM —
                                        تعمل كمنظومة واحدة لتحقيق نمو{' '}
                                        <strong style={{ color: T.ink, fontWeight: 700 }}>حقيقي ومقيس</strong>.
                                    </p>
                                </div>

                                {/* CTAs */}
                                <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                                    <Link
                                        href={register.url()}
                                        style={{
                                            padding: '13px 24px', borderRadius: T.rPill,
                                            background: T.ink, color: '#FAF8FF',
                                            fontSize: 15, fontWeight: 700, textDecoration: 'none',
                                            display: 'inline-flex', alignItems: 'center',
                                            transition: 'transform .15s, box-shadow .15s',
                                        }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 10px 32px rgba(14,11,46,0.22)'; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                                    >
                                        ابدأ Growth Engine مجاناً
                                    </Link>
                                    <a
                                        href="#process"
                                        style={{
                                            padding: '13px 24px', borderRadius: T.rPill,
                                            background: 'transparent', color: T.ink,
                                            border: `1px solid ${T.ink20}`,
                                            fontSize: 15, fontWeight: 600, textDecoration: 'none',
                                            display: 'inline-flex', alignItems: 'center',
                                            transition: 'transform .15s, border-color .15s',
                                        }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.borderColor = T.ink70; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.borderColor = T.ink20; }}
                                    >
                                        شاهد آلية العمل ↓
                                    </a>
                                </div>

                                {/* Stats */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${STATS.length}, 1fr)`,
                                    gap: isMobile ? 8 : 0,
                                }}>
                                    {STATS.map((s, i) => (
                                        <div key={i} style={{
                                            padding: isMobile ? '10px 0' : '0 18px',
                                            borderLeft: !isMobile && i < STATS.length - 1 ? `1px solid ${T.line}` : 'none',
                                            ...(!isMobile && i === 0 ? { paddingRight: 0 } : {}),
                                            minWidth: 0,
                                        }}>
                                            <div style={{ fontSize: 22, fontWeight: 900, color: T.ink, letterSpacing: '-0.03em', lineHeight: 1, whiteSpace: 'nowrap' }}>{s.n}</div>
                                            <div style={{ fontSize: 10, color: T.ink50, marginTop: 4, lineHeight: 1.35 }}>{s.l}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* ── Campaign Mosaic (LEFT in RTL) — hidden on mobile ── */}
                            {!isMobile && (
                                <div ref={mosaicRef}>
                                    <CampaignMosaic />
                                </div>
                            )}
                        </div>

                        {/* Trust bar */}
                        <div ref={trustRef} style={{ marginTop: 32, paddingTop: 22, borderTop: `1px solid ${T.line}`, opacity: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: T.ink50, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>
                                يثق بنا أكثر من 80 عميل في المنطقة
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 44, flexWrap: 'wrap' }}>
                                {['STC', 'NEOM', 'noon', 'Tamara', 'Jahez', 'Mrsool'].map(b => (
                                    <span key={b} style={{ fontSize: 17, fontWeight: 800, color: T.ink, opacity: 0.25, letterSpacing: '-0.01em' }}>{b}</span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Scroll cue */}
                    <div style={{
                        position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                        animation: 'v2-dot-bounce 2.2s ease-in-out infinite',
                    }}>
                        <div style={{ width: 1, height: 28, background: `linear-gradient(to bottom, transparent, ${T.brand})` }} />
                        <div style={{ width: 4, height: 4, borderRadius: '50%', background: T.brand, opacity: 0.65 }} />
                    </div>
                </section>
            </div>
        </>
    );
}
